/**
 * Event Service Hooks
 * React hooks for accessing event management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all event-related services
 * @returns EventServices object containing all event service instances
 */
export function useEventsServices() {
    const { services } = useCCMS();
    if (!services?.events) {
        throw new Error('Event services not available. Ensure CCMSProvider is configured with event services.');
    }
    return services.events;
}
/**
 * Hook to access EventService
 */
export function useEventService() {
    return useEventsServices().event;
}
/**
 * Hook to access EventTypeService
 */
export function useEventTypeService() {
    return useEventsServices().eventType;
}
/**
 * Hook to access EventStatusService
 */
export function useEventStatusService() {
    return useEventsServices().eventStatus;
}
/**
 * Hook to access EventVendorStatusService
 */
export function useEventVendorStatusService() {
    return useEventsServices().eventVendorStatus;
}
/**
 * Hook to access EventVendorRegistrationService
 */
export function useEventVendorRegistrationService() {
    return useEventsServices().eventVendorRegistration;
}
/**
 * Hook to access EventVendorSettingService
 */
export function useEventVendorSettingService() {
    return useEventsServices().eventVendorSetting;
}
/**
 * Hook to access ShopService (public read-only shop endpoints)
 */
export function useShopService() {
    return useEventsServices().shop;
}
/**
 * Hook to access EventAdminService (Events hub admin list, detail, detach, cancel, restore, review)
 */
export function useEventAdminService() {
    return useEventsServices().eventAdmin;
}
/**
 * Hook to access EventSourceService (Planning Center connection, runs and mappings)
 */
export function useEventSourceService() {
    return useEventsServices().eventSource;
}
/**
 * Hook to access EventCalendarService (church calendar and Carpool visibility)
 */
export function useEventCalendarService() {
    return useEventsServices().eventCalendar;
}
/**
 * Hook to access EventLinkService (links, coordinator, an event's tags and reverse lookups)
 */
export function useEventLinkService() {
    return useEventsServices().eventLink;
}
/**
 * Hook to access EventTagService (source tags and manual CCMS tags)
 */
export function useEventTagService() {
    return useEventsServices().eventTag;
}
/**
 * Hook to access EventRuleService (link rules, preview and re-evaluate)
 */
export function useEventRuleService() {
    return useEventsServices().eventRule;
}
/**
 * Hook to access EventLearningService (learned rule proposals and learning settings)
 */
export function useEventLearningService() {
    return useEventsServices().eventLearning;
}
/**
 * Hook to access EventPlaybookService (playbooks and playbook suggestions for an event)
 */
export function useEventPlaybookService() {
    return useEventsServices().eventPlaybook;
}
/**
 * Hook to access EventPlanService (an event's plans: draft, edit, approve, retry and skip)
 */
export function useEventPlanService() {
    return useEventsServices().eventPlan;
}
//# sourceMappingURL=useEventsServices.js.map